// DB-8 — Declarative "capture upstream" config.
//
// When a planner flips an About You toggle (hasChildren / owns_property / hasPets)
// to Yes, an inline capture form appears below the toggle so the detail is entered
// once and seeded into the downstream section — no double-entry. The system is
// DECLARATIVE: each entry below defines what a toggle captures and where it goes;
// the single shared <CaptureUpstreamRenderer> renders/handles them all. Adding a
// new capture (spouse → beneficiary, business → business docs, hasDependents, …)
// is a config entry here, NOT new component code.
//
// `targetCategory` is the category slug that owns `targetSection` — the renderer
// resolves category → section → fields → entries through the standard endpoints.

import { SECTION_SLUGS } from "./sections";
import { CATEGORY_SLUGS } from "./categories";
import { DEPENDENT_RELATIONSHIP_CHOICES } from "./dependent-relationships";
import { PROP_TYPE_OPTIONS } from "./property-types";

// s109 (Build 4b): "address" captures a single-line street entry that the renderer
// wraps into a structured address JSON on write (A-lite) — keeps the mini-list row
// compact while writing valid data into an `address`-type target field (e.g. Properties).
export type CaptureFieldType = "text" | "date" | "select" | "address";

export type CaptureFieldDef = {
  /** fieldKey in the target section. */
  key: string;
  /** Display label in the mini-list. */
  label: string;
  fieldType: CaptureFieldType;
  /** Choices for `select` fields. */
  options?: string[];
  /** s171: optional example shown in the empty input. Added for the pets/livestock widening,
   *  where one field has to read naturally for both a named pet and a described herd — the
   *  label alone could not carry that. Declarative like the rest of this config; the shared
   *  renderer reads it, so no per-capture component code. */
  placeholder?: string;
};

/** s110 (Build 4c / AY-3): a mini-list may ALSO seed one plain row into a SECOND
 *  section in another category on each row-create — the mini-list's identifier field
 *  (first field, e.g. child name) copied across (child name → a Government IDs
 *  `person_name` row). The companion row is a PLAIN row — written through the same
 *  generic_entries upsert, so `is_seeded=false`, exactly like a planner manually typing
 *  the name into a new row. It NEVER shades and NEVER touches completion/section-status
 *  logic. Its rowId is deterministic (`${rowIdPrefix}${childRowId}`) so re-adding/editing
 *  a child never duplicates it; the prefix is DISTINCT from the `__seeded__` naming the
 *  template validator/heal scripts watch for. Planner-initiated on row-add ONLY. */
export type CompanionSeedDef = {
  /** Category slug owning the companion section. */
  targetCategory: string;
  /** Companion section slug the row is written into. */
  targetSection: string;
  /** fieldKey in the companion section that receives the mirrored value. */
  targetField: string;
  /** BENEFICIARY-FILL-1 (s156): mini-list fieldKeys whose values are space-joined into the
   *  companion targetField (e.g. ["first_name","surname"] → a full name in Gov IDs person_name).
   *  Absent → the mini-list's first field alone (legacy single-identifier behaviour). Editing
   *  any listed key re-syncs the companion. */
  companionSourceKeys?: string[];
  /** Deterministic rowId prefix; combined with the source row's rowId. NOT "__seeded__". */
  rowIdPrefix: string;
};

/** Deterministic companion rowId derived from the source (mini-list) row's rowId. */
export function companionRowId(prefix: string, sourceRowId: string): string {
  return `${prefix}${sourceRowId}`;
}

/** Capture a repeating list (children, pets) inline → one downstream row each. */
export type MiniListCapture = {
  type: "mini-list";
  /** About You yes_no fieldKey that gates this capture. */
  toggleField: string;
  /** Category slug owning the target section. */
  targetCategory: string;
  /** Target section slug the rows are written into. */
  targetSection: string;
  /** Fields captured inline (and written to each new row). */
  fields: CaptureFieldDef[];
  /** e.g. "+ Add child". */
  addLabel: string;
  /** e.g. "child" — used in empty-state / a11y copy. */
  recordNoun: string;
  /** s110 (Build 4c): optionally also seed a plain row into a second section on row-create. */
  companionSeed?: CompanionSeedDef;
};

/** Copy a single About You value into one identified downstream row (no UI). */
export type FieldCopyCapture = {
  type: "field-copy";
  /** About You yes_no fieldKey that gates this capture. */
  toggleField: string;
  /** About You fieldKey to copy FROM. */
  sourceField: string;
  /** Category slug owning the target section. */
  targetCategory: string;
  /** Target section slug. */
  targetSection: string;
  /** fieldKey in the target section to copy INTO. */
  targetField: string;
  /** Identify which row to copy into, by fieldKey → value (e.g. prop_type = Primary Residence). */
  targetRowMatch: Record<string, string>;
  /** Always true — never overwrite an existing downstream value. */
  onlyIfEmpty: true;
};

/** s109 (Build 4a): a value-conditional field-copy driven by an About You SELECT
 *  (e.g. "Do you own or rent your home?"). Each branch fires only when the select
 *  equals `whenValue`; a value matching no branch (e.g. "Neither") seeds nothing.
 *  Own matches an existing scaffold row (`targetRowMatch`); Rent seeds a deterministic
 *  `seedRowId` (idempotent — Own↔Rent switching never duplicates). Seed-and-leave: the
 *  renderer only seeds the active branch and never removes the other branch's row. */
export type SelectFieldCopyBranch = {
  /** The select value that activates this branch. */
  whenValue: string;
  targetCategory: string;
  targetSection: string;
  /** fieldKey in the target section to copy INTO. */
  targetField: string;
  /** Match an existing row by fieldKey → value (e.g. prop_type = Primary Residence). */
  targetRowMatch?: Record<string, string>;
  /** Deterministic rowId to seed when there's no scaffold to match (append target). */
  seedRowId?: string;
  /** When true, never overwrite an existing downstream value (fill-if-empty). When false, the branch
   *  MIRRORS the source on every change (one-way sync) — D-154.24: home_tenure="Own" mirrors
   *  home_address into the Primary Residence row's address so "your home appears in Properties". */
  onlyIfEmpty: boolean;
};

export type SelectFieldCopyCapture = {
  type: "select-field-copy";
  /** About You SELECT fieldKey that gates this capture. */
  selectField: string;
  /** About You fieldKey to copy FROM. */
  sourceField: string;
  branches: SelectFieldCopyBranch[];
};

/** s111 (Build 5, D-110.4): a SELECT whose different values open DIFFERENT inline
 *  mini-lists (or none). work_situation: "Self-employed" → Business mini-list; "Employed"
 *  → Employment History mini-list; "Retired" / "Not currently working" → no branch →
 *  nothing seeded. Mirrors select-field-copy's branch shape, but each branch renders a
 *  full mini-list (add/edit/remove) rather than a one-shot copy. No companionSeed. */
export type SelectMiniListBranch = {
  /** The select value that activates this branch. */
  whenValue: string;
  /** Category slug owning the target section. */
  targetCategory: string;
  /** Target section slug the rows are written into. */
  targetSection: string;
  /** Fields captured inline (and written to each new row). */
  fields: CaptureFieldDef[];
  /** e.g. "+ Add business". */
  addLabel: string;
  /** e.g. "business" — used in empty-state / a11y copy. */
  recordNoun: string;
};

export type SelectMiniListCapture = {
  type: "select-mini-list";
  /** About You SELECT fieldKey that gates this capture. */
  selectField: string;
  branches: SelectMiniListBranch[];
};

/** s111 (Build 5): the work_situation value meaning the planner runs a business — it
 *  drives `clients.owns_business` (section-gating for Business & Self-Employment). */
export const WORK_SITUATION_SELF_EMPLOYED = "Self-employed";
/** Map a work_situation value → `clients.owns_business` (Self-employed = true, else false). */
export function deriveOwnsBusiness(workSituation: string | null | undefined): boolean {
  return (workSituation ?? "").trim() === WORK_SITUATION_SELF_EMPLOYED;
}

export type CaptureUpstreamDef =
  | MiniListCapture
  | FieldCopyCapture
  | SelectFieldCopyCapture
  | SelectMiniListCapture;

export const CAPTURE_UPSTREAM: CaptureUpstreamDef[] = [
  {
    // s108 (DB-S5): unified Children & Dependents. Still gated by `hasChildren` (now
    // relabeled "Do you have any children or dependents?"); seeds name + DOB +
    // relationship into the one unified C&D section. The relationship select renders
    // via the generic CaptureUpstreamRenderer select branch (no renderer change).
    type: "mini-list",
    toggleField: "hasChildren",
    targetCategory: CATEGORY_SLUGS.CHILDREN_AND_DEPENDENTS,
    targetSection: SECTION_SLUGS.CHILDREN_DEPENDENTS_OVERVIEW,
    // BENEFICIARY-FILL-1 (s156): child name split → first_name + surname (74A Part 6 columns).
    fields: [
      { key: "first_name", label: "First name(s)", fieldType: "text" },
      { key: "surname", label: "Surname", fieldType: "text" },
      { key: "dob", label: "Date of Birth", fieldType: "date" },
      {
        key: "relationship",
        label: "Relationship",
        fieldType: "select",
        // s147: shared single source (D-140 — was a duplicate of the spec's option list).
        options: [...DEPENDENT_RELATIONSHIP_CHOICES],
      },
    ],
    addLabel: "+ Add child or dependant",
    recordNoun: "child or dependant",
    // s110 (Build 4c / AY-3): on child-add, also seed a plain Government IDs row with the
    // child's name in `person_name` (every other field blank) — identical to the planner
    // typing the name into a new Gov IDs row. No completion/section-status change; the row
    // is plain (is_seeded=false), not shaded. Deterministic `__childgovid__<childRowId>`
    // keeps it idempotent + distinct from the reconciler's `__seeded__` rows.
    companionSeed: {
      targetCategory: CATEGORY_SLUGS.PERSONAL_INFORMATION,
      targetSection: SECTION_SLUGS.PERSONAL_INFORMATION_GOVERNMENT_IDS,
      targetField: "person_name",
      // BENEFICIARY-FILL-1 (s156): mirror the FULL child name (first + surname) into the Gov IDs row.
      companionSourceKeys: ["first_name", "surname"],
      rowIdPrefix: "__childgovid__",
    },
  },
  {
    type: "mini-list",
    toggleField: "hasPets",
    targetCategory: CATEGORY_SLUGS.PERSONAL_INFORMATION,
    targetSection: SECTION_SLUGS.FAMILY_PETS,
    // s171 Block 3b item 4: the toggle question was widened, because this capture presented a
    // pet-shaped row — so a farm answered Yes and was then asked to name one animal at a time from a
    // dog/cat list. Widened in place: no new fields, no new section, and the vet/care fields
    // downstream serve livestock unchanged.
    //
    // s202 (D-202.5): the question now reads "Any pets, or other animals you look after?" — the
    // wording moved on and this comment quoted the retired version. The planner-facing strings below
    // follow it: a planner who is never shown the word "livestock" in the question should not meet it
    // in the add button two lines later. The pet_type OPTIONS keep "Other livestock" by ruling (rows
    // retire, option lists do not — D-177.9c), and the executor task keeps its own title.
    fields: [
      // "Name or description" carries both modes — a pet has a name, a herd has a description.
      { key: "pet_name", label: "Name or description", fieldType: "text", placeholder: 'Biscuit, or "40 beef cattle"' },
      {
        key: "pet_type",
        label: "Type",
        fieldType: "select",
        // Livestock options APPENDED after the pet types — existing rows keep their stored value,
        // and the familiar options stay where a pet owner expects them.
        //
        // 🔴 s224 (D-223.36 ruling 3): this list and `family-pets.pet_type` are TWO SURFACES ONTO
        // THE SAME ROWS — the capture seeds straight into the section — so they must offer the same
        // vocabulary or a value picked here fails to resolve there (`pets-livestock-capture.test.ts`
        // pins the equality, order included). The board now owns the list, so it shrinks HERE too.
        // This is act b of the retirement for `pet_type`: a writer, repointed, not assumed.
        options: [
          "Dog", "Cat", "Bird", "Horse", "Other", "Cattle", "Small animal or reptile",
        ],
      },
    ],
    addLabel: "+ Add a pet or other animal",
    recordNoun: "pet or other animal",
  },
  {
    // s111 (Build 5, D-110.4): work_situation drives DIFFERENT captures per value —
    // replaces the retired owns_business mini-list. "Self-employed" → the Business &
    // Self-Employment mini-list (same fields owns_business seeded); "Employed" → an
    // Employment History mini-list (employer + job_title); "Retired" / "Not currently
    // working" match no branch → nothing seeded. Self-employed ALSO flips
    // clients.owns_business via situation-flags (GenericCategoryPage) — see deriveOwnsBusiness.
    type: "select-mini-list",
    selectField: "work_situation",
    branches: [
      {
        whenValue: WORK_SITUATION_SELF_EMPLOYED, // "Self-employed"
        targetCategory: CATEGORY_SLUGS.FINANCIAL_AND_INSURANCE,
        targetSection: SECTION_SLUGS.FINANCIAL_BUSINESS_SELF_EMPLOYMENT,
        fields: [
          { key: "biz_name", label: "Business name", fieldType: "text" },
          {
            key: "biz_type",
            label: "Type",
            fieldType: "select",
            options: ["Sole Proprietorship", "Partnership", "Corporation", "Self-Employed / Freelance", "Farm", "Holding Company", "Other"],
          },
        ],
        addLabel: "+ Add business",
        recordNoun: "business",
      },
      {
        whenValue: "Employed",
        targetCategory: CATEGORY_SLUGS.PERSONAL_INFORMATION,
        targetSection: SECTION_SLUGS.PERSONAL_INFORMATION_CAREER,
        fields: [
          { key: "employer", label: "Employer", fieldType: "text" },
          { key: "job_title", label: "Job title", fieldType: "text" },
        ],
        addLabel: "+ Add employer",
        recordNoun: "employer",
      },
    ],
  },
  {
    // s109 (Build 4b): "Do you own any other property?" — property BEYOND the home.
    // Converted from the former field-copy (home→Primary Residence, now owned by the
    // Build 4a home_tenure Own branch) into a mini-list that appends real Properties
    // rows. address is captured single-line and wrapped to address JSON on write
    // (A-lite); prop_type mirrors the Properties select exactly. Properties also
    // requires title_ownership → a captured row correctly shows "needs attention"
    // until the planner sets it (seeded data counts like typed — Build 3 reverted).
    type: "mini-list",
    toggleField: "owns_property",
    targetCategory: CATEGORY_SLUGS.ASSETS,
    targetSection: SECTION_SLUGS.ASSETS_PROPERTIES,
    fields: [
      { key: "address", label: "Address", fieldType: "address" },
      {
        key: "prop_type",
        label: "Type",
        fieldType: "select",
        options: [...PROP_TYPE_OPTIONS], // FIELD-AUDIT-1 (D-154.8): single source — was a stale hardcoded duplicate of the old list
      },
    ],
    addLabel: "+ Add property",
    recordNoun: "property",
  },
  {
    // s109 (Build 4a): "Do you own or rent your home?" select. Own → fill the Primary
    // Residence scaffold in Properties (match); Rent → seed the home address into a
    // deterministic Things I Rent row (append, idempotent). "Neither" matches no branch
    // → seeds nothing. Seed-and-leave on Own↔Rent switch (never removes the prior row).
    type: "select-field-copy",
    selectField: "home_tenure",
    sourceField: "home_address",
    branches: [
      {
        whenValue: "Own",
        targetCategory: CATEGORY_SLUGS.ASSETS,
        targetSection: SECTION_SLUGS.ASSETS_PROPERTIES,
        targetField: "address",
        // s234 (CH2-STORAGE-PR3, D-233.70(2)): the home is the row flagged as where the planner lives — matched on the
        // FIRST entry (is_residence), so a home whose kind was changed still mirrors; a new home is made with every entry.
        targetRowMatch: { is_residence: "true", prop_type: "Primary Residence" },
        // D-154.24: MIRROR (not fill-if-empty). While home_tenure="Own", the Primary Residence row's
        // address tracks home_address on every change — the planner's mental model is "my home shows
        // up in Properties". Only the address field is written; est. value + other Primary-row fields
        // are untouched. Pre-launch (no real users) the overwrite is intended. The re-arm on
        // sourceValue change (D-154.20 A) is what makes each later edit re-sync.
        onlyIfEmpty: false,
      },
      {
        whenValue: "Rent",
        targetCategory: CATEGORY_SLUGS.FINANCIAL_AND_INSURANCE,
        targetSection: SECTION_SLUGS.FINANCIAL_AND_INSURANCE_HOUSING,
        targetField: "rental_address",
        seedRowId: "__capture__home-rental",
        onlyIfEmpty: true,
      },
    ],
  },
];

/** The capture def (if any) gated by a given About You field key — the yes_no toggle
 *  (`toggleField`) for mini-list/field-copy, or the SELECT (`selectField`) for
 *  select-field-copy (s109 Build 4a). */
export function captureForToggle(fieldKey: string): CaptureUpstreamDef | undefined {
  return CAPTURE_UPSTREAM.find((c) => {
    if (c.type === "select-field-copy" || c.type === "select-mini-list") return c.selectField === fieldKey;
    return c.toggleField === fieldKey;
  });
}
